import styled from 'styled-components'
import { Select } from 'antd'
import { useTranslation, Trans } from 'react-i18next'
import { useState, useMemo } from 'react'
import PropTypes from 'prop-types'
import Form from '$components/Form'
import Tooltip from '$components/Tooltip'
const { Option } = Select

const Wrapper = styled.div`
  // using classes to select children of Wrapper to override antd styles
  .ant-form-item-control-input-content {
    display: 'flex';
  }

  .property-select {
    width: ${({ $units }) => $units && Object.keys($units).length > 0 && '68%'};
    margin-right: 2%;
  }

  .unit-select {
    width: 30%;
  }
`

function stripSeparator(value, numberSeparator) {
  if (value == null || value === undefined || value === '') return value
  const str = String(value).trim()
  if (numberSeparator === 'comma_decimal') {
    return str.replaceAll('.', '').replace(',', '.')
  } else {
    return str.replaceAll(',', '')
  }
}

function isFloatOrNone(value) {
  if (value === null || value === undefined || value === '') {
    return true
  }
  const num = parseFloat(value)
  return !Number.isNaN(num) && Number.isFinite(num) && num > 0
}

function isInt(value) {
  const num = parseInt(value, 10)
  return (
    !Number.isNaN(num) &&
    Number.isInteger(num) &&
    String(num) === String(value).trim() &&
    num > 0
  )
}

function isNonNegativeInt(value) {
  const num = parseInt(value, 10)
  return (
    !Number.isNaN(num) &&
    Number.isInteger(num) &&
    String(num) === String(value).trim() &&
    num >= 0
  )
}

function isEmpty(value) {
  if (value === null || value === undefined || value === '') {
    return true
  }
  return false
}

function isIntOrNone(value) {
  if (value === null || value === undefined || value === '') {
    return true
  }
  return isInt(value)
}

function isNonNegativeIntOrNone(value) {
  if (value === null || value === undefined || value === '') {
    return true
  }
  return isNonNegativeInt(value)
}

const PropertyMapper = ({
  name,
  label,
  required = false,
  propertiesToMap,
  disableOptions = [],
  units,
  data,
  useOptions = () => ({}),
  numberSeparator = 'point_decimal',
}) => {
  const { t } = useTranslation()
  const unitFormItemName = `${name}-unit`
  const [currentValue, setCurrentValue] = useState()

  const { data: options, isLoading: optionsAreLoading } = useOptions()

  const warnIfEmpty = ['Width', 'Height', 'Depth']

  const hasEmptyValues = useMemo(() => {
    if (!warnIfEmpty.includes(name)) return false
    if (!currentValue) return false
    return data.some((item) => isEmpty(item[currentValue]))
  }, [data, name, currentValue, numberSeparator])

  if (optionsAreLoading) {
    return 'Options are loading...'
  }

  let rules = []
  if (required) {
    rules.push({
      required: true,
      message: t('parts_master_data.upload.required_field_prompt'),
    })
  }

  if (options) {
    rules.push({
      validator: (_, columnName) => {
        if (columnName) {
          for (const { [columnName]: value } of data) {
            if (!value) {
              // ignore empty cells
              continue
            }
            if (!options.some((option) => option.name === value)) {
              const msg = (
                <Trans
                  i18nKey="parts_master_data.upload.unknown_material_warning"
                  components={{
                    1: <br />,
                    value,
                    options: options.map((option) => option.name).join(', '),
                  }}
                />
              )
              return Promise.reject(msg)
            }
          }
        }
        return Promise.resolve()
      },
    })
  }

  return (
    <Wrapper $units={units}>
      <Form.Item
        label={t(label || name)}
        validateStatus={hasEmptyValues ? 'warning' : undefined}
        help={
          hasEmptyValues
            ? t('parts_master_data.upload.empty_field_warning', {
                field: name,
              })
            : null
        }
      >
        <Form.Item name={name} noStyle={true} rules={rules}>
          <Select
            className="property-select"
            placeholder={t('parts_master_data.upload.select_placeholder')}
            allowClear={true}
            placement="bottomLeft"
            virtual={false}
            onChange={setCurrentValue}
          >
            {[...propertiesToMap]
              // sort array to show unmapped properties first
              .sort(
                (a, b) =>
                  disableOptions.includes(a) - disableOptions.includes(b),
              )
              .map((property) => {
                const alreadyMapped =
                  disableOptions.includes(property) && property !== currentValue

                const mustBeFloatOrEmpty = ['Price', 'Price AM']
                const mustBeIntOrEmpty = ['Weight']
                const mustBeNonNegativeIntOrEmpty = ['Width', 'Height', 'Depth']
                var typeIsCastable = true

                if (mustBeFloatOrEmpty.includes(name)) {
                  typeIsCastable = data.every((item) =>
                    isFloatOrNone(
                      stripSeparator(item[property], numberSeparator),
                    ),
                  )
                }
                if (mustBeIntOrEmpty.includes(name)) {
                  typeIsCastable = data.every((item) =>
                    isIntOrNone(
                      stripSeparator(item[property], numberSeparator),
                    ),
                  )
                }
                if (mustBeNonNegativeIntOrEmpty.includes(name)) {
                  typeIsCastable = data.every((item) =>
                    isNonNegativeIntOrNone(
                      stripSeparator(item[property], numberSeparator),
                    ),
                  )
                }
                return (
                  <Option
                    key={property}
                    value={property}
                    disabled={alreadyMapped || !typeIsCastable}
                    title={
                      alreadyMapped
                        ? t('parts_master_data.upload.column_already_mapped')
                        : !typeIsCastable
                          ? t('parts_master_data.upload.column_wrong_type')
                          : ''
                    }
                  >
                    <Tooltip
                      title={
                        alreadyMapped
                          ? t('parts_master_data.upload.column_already_mapped')
                          : !typeIsCastable
                            ? t('parts_master_data.upload.column_wrong_type')
                            : ''
                      }
                    >
                      {property}
                    </Tooltip>
                  </Option>
                )
              })}
          </Select>
        </Form.Item>
        {units && Object.keys(units).length > 0 && (
          <Form.Item name={unitFormItemName} noStyle={true} initialValue={1}>
            <Select className="unit-select" virtual={false}>
              {Object.keys(units).map((unit) => (
                <Option key={unit} value={units[unit]}>
                  {unit}
                </Option>
              ))}
            </Select>
          </Form.Item>
        )}
      </Form.Item>
    </Wrapper>
  )
}

PropertyMapper.propTypes = {
  numberSeparator: PropTypes.oneOf(['point_decimal', 'comma_decimal']),
}

export default PropertyMapper
